// ===========================================
// Classes Management Page
// ===========================================
// CRUD interface for managing classes and enrollments

import { getStudentAgeGroup } from '../../api/users.api';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isTeacherViewPath } from '../../components/layout/effectiveView';
import { useHistoryEntryState } from '../../hooks/useHistoryEntryState';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import { safeHexColor } from '../../utils/safeColor';
import {
  useLocations,
  useRooms,
  useLessonTypes,
  useLessonDurations,
  useProgramLevels,
  useStreams,
} from '../../hooks/useAdmin';
import { useTeachers } from '../../hooks/useUsers';
import { useTermLock } from '../../hooks/useTermLock';
import { useViewTerms } from '../../hooks/useViewTerms';
import { useViewInstruments } from '../../hooks/useViewOptions';
import { usePermissions } from '../../hooks/usePermissions';
import { useBillingNotice } from '../../hooks/useBillingNotice';
import { isTermLockedError, termLockedReason } from '../../utils/termLock';
import {
  useLessons,
  useCreateLesson,
  useBulkCreateLessons,
  useUpdateLesson,
  useDeleteLesson,
} from '../../hooks/useLessons';
import {
  LessonFilters,
  CreateLessonInput,
  getShortDayName,
  formatTime,
  getLessonTypeColor,
  calculateEndTime,
} from '../../api/lessons.api';
import {
  characterAgeGroupFromName,
  getCharacterName,
  getCharacterColor,
} from '../../components/brand/CharacterIllustration.utils';
import {
  LessonFormData,
  GroupedLesson,
  DEFAULT_FORM_DATA,
  groupLessonsByProperties,
  filterTermsByScope,
  termsToRemoveForEdit,
  hybridPatternUnchanged,
  classWeeksUnchanged,
  ownClassWeeks,
  storedWindowOf,
  windowUnchanged,
  sameWindow,
  windowForUpdate,
  windowForCreate,
  parseWindowHasData,
  isPartnerEligible,
  resolveAnchorTermId,
  findCurrentTermId,
  LessonFiltersPanel,
  LessonDialog,
  EditScopeModal,
  PartnerOfferDialog,
  TimeOffWarningDialog,
  WindowHasDataDialog,
} from '../../components/admin/lessons';
import type { EditScope, WindowHasDataInfo, ClassWeeksPatternType } from '../../components/admin/lessons';
import InvoicesToAmendDialog, { type InvoicesToAmendContext } from '../../components/invoices/InvoicesToAmendDialog';
import { classWeeksForWording } from '../../utils/invoiceAmend';
import type { InvoiceToAmend } from '../../api/invoices.api';
import { lessonWeekWindow, termWeekCount } from '../../utils/dateUtils';

// ===========================================
// COMPONENT
// ===========================================

export default function LessonsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Determine base path for navigation (admin vs teacher) with school slug
  const schoolSlug = extractSchoolSlugFromPath(location.pathname);
  const slugPrefix = schoolSlug ? `/${schoolSlug}` : '';
  const basePath = isTeacherViewPath(location.pathname) ? `${slugPrefix}/teacher` : `${slugPrefix}/admin`;
  const isTeacherView = basePath.endsWith('/teacher');

  const { data: teachersData } = useTeachers();
  const teachers = teachersData ?? [];

  // Filters state
  const [filters, setFilters] = useState<LessonFilters>({});

  // View mode for teachers: 'mine' or 'all'
  const [viewMode, setViewMode] = useState<'mine' | 'all'>('mine');

  // Handle view mode toggle
  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'mine' | 'all' | null) => {
    if (newMode) {
      setViewMode(newMode);
    }
  };

  // Invalidate lessons cache when view mode changes to force refetch
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['lessons'] });
  }, [viewMode, queryClient]);

  // Data queries
  // The teacher view cannot read /admin/terms: without this the term filter was empty, the
  // current-term default never ran and every term read as unlocked (#272).
  const { data: termsData } = useViewTerms();
  // The admin lists run only for a caller their routes admit (#272). Locations, rooms, durations,
  // program levels and streams feed only the class dialog, which a teacher cannot open; the
  // instrument filter falls back to the `/calendar` list teachers may read.
  const { can } = usePermissions();
  const canReadSchoolSetup = can('school_setup', 'read');
  const canReadClassConfig = can('class_config', 'read');
  const { data: locationsData } = useLocations({ enabled: canReadSchoolSetup });
  const { data: instrumentsData } = useViewInstruments();
  const { data: lessonTypesData } = useLessonTypes({ enabled: canReadClassConfig });
  const { data: durationsData } = useLessonDurations({ enabled: canReadClassConfig });
  const { data: programLevelsData } = useProgramLevels({ enabled: canReadClassConfig });
  const { data: streamsData } = useStreams({ enabled: canReadClassConfig });

  const terms = useMemo(() => termsData ?? [], [termsData]);
  // Lock state (the school-day mirror of the backend term lock): a term is editable until the NEXT
  // term starts. Called once here and passed down, so the pure helpers stay provider-free.
  const { isLocked, stateOf } = useTermLock(terms);
  const lockReasonOf = useCallback(
    (termId: string): string => {
      const term = terms.find((t) => t.id === termId);
      const state = stateOf(termId);
      return term && state ? termLockedReason(term, state) : '';
    },
    [terms, stateOf]
  );
  // "(Ended)" label: the SCHOOL's day via the lock state (a locked/reopened term has necessarily ended).
  const isEnded = useCallback(
    (termId: string): boolean => {
      const state = stateOf(termId);
      return !!state && (state.status !== 'OPEN' || state.ended);
    },
    [stateOf]
  );
  const locations = locationsData ?? [];
  const instruments = instrumentsData ?? [];
  const programLevels = programLevelsData ?? [];
  const streams = streamsData ?? [];
  // Memoize to avoid reference changes affecting downstream useMemo
  const lessonTypes = useMemo(() => lessonTypesData ?? [], [lessonTypesData]);
  const durations = durationsData ?? [];

  // Default the Term filter to the CURRENT term once the terms have loaded — opening on every
  // term across every year is confusing (it lists classes from years ago alongside live ones).
  // ONE-SHOT by design (`didSeedTermFilter`): the admin can still pick "All Terms", and without
  // the guard this would immediately re-apply the current term and make that choice unselectable.
  // Skipped when a filter is already set (e.g. a termId arrived via navigation state).
  const didSeedTermFilter = useRef(false);
  // The class window the edit dialog was SEEDED with. "Did the admin change the window?" is
  // answered against this as well as the anchor row: if the seed could not see the anchor's
  // row (terms not loaded yet), an untouched form must still send NO window — otherwise a
  // room edit would silently clear the class's start week (/qa F2).
  const seededWindowRef = useRef<{ startWeek: number | null; endWeek: number | null }>({ startWeek: null, endWeek: null });
  useEffect(() => {
    if (didSeedTermFilter.current || terms.length === 0) return;
    didSeedTermFilter.current = true;
    const currentTermId = findCurrentTermId(terms);
    // No current/upcoming term (all history) → leave it on All Terms rather than an arbitrary pick.
    if (currentTermId) {
      setFilters((prev) => (prev.termId ? prev : { ...prev, termId: currentTermId }));
    }
  }, [terms]);

  // Build effective filters. Teacher view: "My classes" asks for `mine` (a caller holding
  // scheduling.read, an ADMIN or a staff role, would otherwise get the whole school; roles
  // R12) and "All" passes allClasses (a plain teacher's coverage view).
  const effectiveFilters = useMemo(() => {
    const filtersCopy = { ...filters };
    if (isTeacherView && viewMode === 'all') {
      filtersCopy.allClasses = true;
    } else if (isTeacherView) {
      filtersCopy.mine = true;
    }
    return filtersCopy;
  }, [filters, isTeacherView, viewMode]);

  // Data queries - fetch lessons with effective filters
  const { data: lessonsData, isLoading, error } = useLessons(effectiveFilters);

  // Group lessons by their core properties to show each unique lesson once
  const groupedLessons = useMemo(
    () => groupLessonsByProperties(lessonsData ?? [], isLocked),
    [lessonsData, isLocked]
  );

  // Get rooms based on selected location
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const { data: roomsData } = useRooms(selectedLocationId || undefined, { enabled: canReadSchoolSetup });
  const rooms = roomsData ?? [];

  // Mutations
  const createMutation = useCreateLesson();
  const bulkCreateMutation = useBulkCreateLessons();
  const updateMutation = useUpdateLesson();
  const billingNotice = useBillingNotice();
  const deleteMutation = useDeleteLesson();

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState<GroupedLesson | null>(null);
  const [formData, setFormData] = useState<LessonFormData>(DEFAULT_FORM_DATA);
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [overrideConflicts, setOverrideConflicts] = useState(false);
  // Cross-term edit scope: modal shown when editing a class spanning 2+ terms.
  const [scopeModalOpen, setScopeModalOpen] = useState(false);
  // Offer-to-partner: set when a save 409s against a partner-ELIGIBLE lesson — instead of the bare
  // conflict toast, offer to pair them. Carries the conflicting lesson + the scope to re-submit with.
  const [partnerOffer, setPartnerOffer] = useState<
    { conflictLessonId: string; conflictLessonName: string; scope: EditScope } | null
  >(null);
  // Teacher time off: set when a save 409s against APPROVED time off. Unlike a real double-booking
  // (which stays a hard red block), this is an amber "schedule anyway?" warning — the admin can
  // proceed, which re-submits with allowAwayOverride (NOT overrideConflicts, which would also
  // wave through room/lesson clashes in the same save).
  const [awayOffer, setAwayOffer] = useState<
    {
      teacherName?: string;
      awayLabel?: string;
      awayFrom?: string;
      awayTo?: string;
      scope: EditScope;
    } | null
  >(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState<GroupedLesson | null>(null);
  // Second-stage confirm shown when deleting a BAND lesson that hybrid classes
  // reference — the message names those classes; confirming re-issues with force.
  const [bandDetachMessage, setBandDetachMessage] = useState<string | null>(null);
  // 409 WINDOW_HAS_DATA: a later start / earlier last week over weeks already used.
  const [windowRefusal, setWindowRefusal] = useState<WindowHasDataInfo | null>(null);
  // "An invoice may need amending": sent invoices of the affected families after a class's
  // first / last week changed (D2 — a prompt with links, nothing is amended automatically).
  // Kept with this history entry, so Back from an invoice brings the list back (D-c).
  const [amendPrompt, setAmendPrompt] = useHistoryEntryState<{
    invoices: InvoiceToAmend[];
    context: InvoicesToAmendContext;
  } | null>('lessons-amend-prompt', null);

  // Get selected lesson type
  const selectedLessonType = useMemo(
    () => lessonTypes.find((lt) => lt.id === formData.lessonTypeId),
    [lessonTypes, formData.lessonTypeId]
  );

  // Partner classes: eligible partners for the picker. Same teacher/term/day as the lesson being
  // edited, INDIVIDUAL + allowPartnerOverlap, not the lesson itself, and free (unpaired) OR already
  // paired back to this lesson. The backend re-validates on save; this is the FE convenience filter.
  // The term this edit is anchored to. NOT `editingLesson.termId` — on a class spanning terms that
  // field is only the API-ordering seed and can name an ENDED term (see resolveAnchorTermId). Every
  // term-keyed decision below (partner picker scope, partner write target, conflict-check term,
  // scope-modal label) uses this so they all act on the same live term the update loop writes to.
  const anchorTermId = useMemo(
    () => (editingLesson ? resolveAnchorTermId(editingLesson, terms, isLocked) : ''),
    [editingLesson, terms, isLocked]
  );

  const partnerCandidates = useMemo(() => {
    if (
      !editingLesson ||
      selectedLessonType?.type !== 'INDIVIDUAL' ||
      !selectedLessonType?.allowPartnerOverlap
    ) {
      return [];
    }
    const ownLessonIds = new Set(editingLesson.termLessons.map((tl) => tl.id));
    const self = {
      id: editingLesson.id,
      teacherId: editingLesson.teacherId,
      termId: anchorTermId,
      dayOfWeek: editingLesson.dayOfWeek,
    };
    const candidates = (lessonsData ?? [])
      // Exclude every term-row of the grouped lesson (isPartnerEligible only knows the primary id),
      // then apply the shared eligibility predicate so the picker matches the backend + the offer.
      .filter((l) => !ownLessonIds.has(l.id) && isPartnerEligible(l, self))
      .map((l) => ({
        id: l.id,
        name: l.name,
        dayOfWeek: l.dayOfWeek,
        startTime: l.startTime,
        endTime: l.endTime,
      }));

    // The currently-linked partner must always be selectable even if it's now inactive (soft
    // deleted) or otherwise absent from the candidate list — otherwise the Select would show a
    // value with no matching option (blank + MUI out-of-range warning). Prepend it, marked so the
    // UI can annotate it, when it isn't already present.
    const current = editingLesson.Partner;
    if (current && !candidates.some((c) => c.id === current.id)) {
      candidates.unshift({
        id: current.id,
        name: current.name,
        dayOfWeek: current.dayOfWeek,
        startTime: current.startTime,
        endTime: current.endTime,
        ...(current.isActive ? {} : { inactive: true }),
      });
    }
    return candidates;
  }, [editingLesson, selectedLessonType, lessonsData, anchorTermId]);

  // Update duration and hybrid combination when lesson type changes (new lessons only)
  useEffect(() => {
    if (selectedLessonType && !editingLesson) {
      setFormData((prev) => {
        const updates: Partial<LessonFormData> = {
          durationMins: selectedLessonType.defaultDuration,
          maxStudents: selectedLessonType.type === 'INDIVIDUAL' ? 1 : prev.maxStudents,
        };
        if (selectedLessonType.type === 'HYBRID') {
          updates.hybridCombination = 'GROUP_INDIVIDUAL';
          updates.bandLessonId = '';
          updates.bandWeeks = [];
          updates.groupWeeks = [];
          updates.individualWeeks = [];
        }
        return { ...prev, ...updates };
      });
    }
  }, [selectedLessonType, editingLesson]);

  // Bands may now span multiple terms — one band Lesson row per term, each with its own
  // BandTerm rehearsal config (bulkCreateLessons handles this per-term). The old
  // single-term guard was removed. Note: for CUSTOM rehearsal weeks the SAME week numbers
  // apply to every selected term (weeks mean different dates per term); EVERY_WEEK is
  // re-derived per term server-side. The ClassWeeksConfig surfaces a note when >1 term.

  // Update rooms when location changes
  useEffect(() => {
    if (formData.locationId !== selectedLocationId) {
      setSelectedLocationId(formData.locationId);
      if (formData.locationId !== selectedLocationId) {
        setFormData((prev) => ({ ...prev, roomId: '' }));
      }
    }
  }, [formData.locationId, selectedLocationId]);

  // Handle edit from detail page navigation
  useEffect(() => {
    const state = location.state as { editLessonId?: string } | null;
    // Wait for the terms too: the window is seeded from the ANCHOR term's row, and the
    // anchor cannot be resolved without them (/qa F2).
    if (state?.editLessonId && groupedLessons.length > 0 && terms.length > 0) {
      // Find the grouped lesson that contains this lesson ID
      const lessonToEdit = groupedLessons.find(
        (gl) => gl.id === state.editLessonId || gl.termLessons.some((tl) => tl.id === state.editLessonId)
      );
      if (lessonToEdit) {
        // Open edit modal for this lesson
        setEditingLesson(lessonToEdit);
        setSelectedLocationId(lessonToEdit.Room.Location.id);
        const allTermIds = lessonToEdit.termLessons.map((tl) => tl.termId);
        // Mid-term window: the ANCHOR term's own row, not the arbitrary seeding row
        const seededWindow = storedWindowOf(lessonToEdit, resolveAnchorTermId(lessonToEdit, terms, isLocked));
        seededWindowRef.current = seededWindow;
        setFormData({
          name: lessonToEdit.name,
          description: lessonToEdit.description || '',
          lessonTypeId: lessonToEdit.lessonTypeId,
          termId: lessonToEdit.termId,
          termIds: allTermIds,
          teacherId: lessonToEdit.teacherId,
          locationId: lessonToEdit.Room.Location.id,
          roomId: lessonToEdit.roomId,
          instrumentId: lessonToEdit.instrumentId || '',
          programLevelId: lessonToEdit.programLevelId || '',
          streamId: lessonToEdit.streamId || '',
          hasSchedule: lessonToEdit.hasSchedule ?? true,
          dayOfWeek: lessonToEdit.hasSchedule ? lessonToEdit.dayOfWeek : null,
          startTime: lessonToEdit.hasSchedule ? lessonToEdit.startTime : null,
          durationMins: lessonToEdit.hasSchedule ? lessonToEdit.durationMins : null,
          maxStudents: lessonToEdit.maxStudents,
          isRecurring: lessonToEdit.isRecurring,
          isActive: lessonToEdit.isActive,
          patternType: (lessonToEdit.HybridLessonPattern ?? lessonToEdit.HybridPattern)?.patternType || 'ALTERNATING',
          hybridCombination: (lessonToEdit.HybridLessonPattern ?? lessonToEdit.HybridPattern)?.hybridCombination || 'GROUP_INDIVIDUAL',
          groupWeeks: ((lessonToEdit.HybridLessonPattern ?? lessonToEdit.HybridPattern)?.groupWeeks as number[]) || [],
          individualWeeks: ((lessonToEdit.HybridLessonPattern ?? lessonToEdit.HybridPattern)?.individualWeeks as number[]) || [],
          bandLessonId: (lessonToEdit.HybridLessonPattern ?? lessonToEdit.HybridPattern)?.bandLessonId || '',
          bandWeeks: ((lessonToEdit.HybridLessonPattern ?? lessonToEdit.HybridPattern)?.bandWeeks as number[]) || [],
          individualSlotDuration: (lessonToEdit.HybridLessonPattern ?? lessonToEdit.HybridPattern)?.individualSlotDuration || 30,
          bookingDeadlineHours: (lessonToEdit.HybridLessonPattern ?? lessonToEdit.HybridPattern)?.bookingDeadlineHours || 24,
          // Band term
          classWeeksPatternType: (ownClassWeeks(lessonToEdit)?.patternType as ClassWeeksPatternType) || 'EVERY_WEEK',
          classWeeks: ownClassWeeks(lessonToEdit)?.weeks ?? [],
          // Partner classes
          partnerLessonId: lessonToEdit.partnerLessonId || '',
          ...seededWindow,
        });
        setModalOpen(true);
        // Clear the state to prevent reopening on subsequent renders
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, groupedLessons, navigate, location.pathname, terms, isLocked]);

  // Table columns
  const columns: Column<GroupedLesson>[] = [
    {
      id: 'name',
      label: 'Class',
      minWidth: 130,
      subLabel: 'Day / Time',
      subSortValue: (row) =>
        row.hasSchedule && row.dayOfWeek != null && row.startTime
          ? row.dayOfWeek * 10000 + parseInt(row.startTime.replace(':', ''))
          : 999999,
      format: (value, row) => (
        <Box>
          <Typography
            component="a"
            href={`${basePath}/classes/${row.id}`}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              navigate(`${basePath}/classes/${row.id}`);
            }}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              cursor: 'pointer',
              fontWeight: 500,
              display: 'block',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {value as string}
          </Typography>
          {row.hasSchedule && row.dayOfWeek != null ? (
            <Typography variant="caption" color="text.secondary">
              {getShortDayName(row.dayOfWeek)} &middot; {formatTime(row.startTime!)} - {formatTime(row.endTime!)}
            </Typography>
          ) : (
            <Chip label="Schedule TBD" size="small" variant="outlined" sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }} />
          )}
          {(() => {
            // A hybrid class with a band combination but no band lesson assigned is in a
            // "band TBD" state (its band was deleted) — flag it so it isn't forgotten.
            const pattern = row.HybridLessonPattern ?? row.HybridPattern;
            const isBandTbd =
              !!pattern &&
              pattern.hybridCombination !== 'GROUP_INDIVIDUAL' &&
              !pattern.bandLessonId;
            return isBandTbd ? (
              <Chip
                label="Band: TBD"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ mt: 0.5, ml: 0.5, fontSize: '0.65rem', height: 18 }}
              />
            ) : null;
          })()}
          {row.duplicateTermNames && (
            // Two classes with the same name, teacher, room, day and time in one term are
            // shown as separate rows (never merged); this chip says why they look identical.
            // `describeChild` + a focusable chip so keyboard and screen-reader users get the
            // explanation too, not just mouse hover.
            <Tooltip
              describeChild
              title={`Another class in ${row.duplicateTermNames.join(', ')} has the same name, teacher, room, day and time. Keep one and delete the other — a student enrolled in both is billed twice while both are active (an inactive class is not billed).`}
            >
              <Chip
                label={`Duplicate · ${row.duplicateTermNames.join(', ')}`}
                tabIndex={0}
                size="small"
                color="error"
                variant="outlined"
                sx={{ mt: 0.5, ml: 0.5, fontSize: '0.65rem', height: 18 }}
              />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      id: 'room',
      label: 'Room',
      minWidth: 80,
      sortValue: (row) => row.Room.name,
      format: (_, row) => (
        <Box>
          <Typography variant="body2">{row.Room.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.Room.Location.name}
          </Typography>
        </Box>
      ),
    },
    ...(!isTeacherView || viewMode === 'all' ? [{
      id: 'teacher',
      label: 'Teacher',
      minWidth: 90,
      sortValue: (row: GroupedLesson) => row.Instructor?.User
        ? `${row.Instructor.User.lastName} ${row.Instructor.User.firstName}`
        : '',
      format: (_: unknown, row: GroupedLesson) => (
        <Chip
          label={row.Instructor?.User ? `${row.Instructor.User.firstName} ${row.Instructor.User.lastName}` : 'Unassigned'}
          size="small"
          sx={{
            // See utils/safeColor: `sx` is a CSS-injection sink and the hex
            // guarantee currently lives only in the write validator.
            backgroundColor: safeHexColor(row.Instructor?.color),
            color: 'white',
          }}
        />
      ),
    }] as Column<GroupedLesson>[] : []),
    {
      id: 'enrollments',
      label: 'Students',
      minWidth: 120,
      sortable: false,
      format: (_: unknown, row: GroupedLesson) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {row.LessonEnrollment?.length > 0 ? row.LessonEnrollment.map((enrollment) => {
            const student = enrollment.Student;
            // The lessons API sends PascalCase `AgeGroup`; the camelCase read was
            // always undefined, so every student rendered as Steve. The name is the
            // school's own ("Pre-school"), resolved rather than cast (2026-09-09).
            const ageGroupName = characterAgeGroupFromName(getStudentAgeGroup(student)?.name);
            const characterName = getCharacterName(ageGroupName).toLowerCase();
            const characterColor = getCharacterColor(ageGroupName);
            const avatarSrc = student.avatar
              ? `/avatars/${student.avatar}`
              : `/avatars/${characterName}/${characterName}-happy.svg`;
            return (
              <Box
                key={student.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: characterColor,
                  borderRadius: 3,
                  pl: 0.25,
                  pr: 1,
                  py: 0.25,
                }}
              >
                <Box
                  component="img"
                  src={avatarSrc}
                  alt={student.firstName}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: 'white',
                    border: `1.5px solid ${characterColor}`,
                    objectFit: 'contain',
                    p: 0.25,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'white',
                    fontSize: '0.7rem',
                  }}
                >
                  {student.firstName}
                </Typography>
              </Box>
            );
          }) : (
            <Typography variant="body2" color="text.secondary">No students</Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'instrument',
      label: 'Instrument',
      minWidth: 70,
      width: 90,
      sortValue: (row) => row.Instrument?.name ?? '',
      format: (_, row) =>
        row.Instrument ? (
          <Chip
            label={row.Instrument.name}
            size="small"
            sx={row.Instrument.color ? {
              backgroundColor: row.Instrument.color,
              color: 'white',
            } : undefined}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        ),
    },
    {
      id: 'lessonType.type',
      label: 'Type',
      minWidth: 70,
      width: 85,
      sortValue: (row) => row.LessonType.name,
      format: (_, row) => (
        <Chip
          label={row.LessonType.name}
          size="small"
          color={row.LessonType.color ? undefined : getLessonTypeColor(row.LessonType.type)}
          sx={row.LessonType.color ? {
            backgroundColor: row.LessonType.color,
            color: 'white',
          } : undefined}
        />
      ),
    },
    {
      id: 'programLevel',
      label: 'Level',
      minWidth: 70,
      width: 90,
      sortValue: (row) => row.ProgramLevel?.name ?? '',
      format: (_, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {row.ProgramLevel ? (
            <Chip
              label={row.ProgramLevel.name}
              size="small"
              sx={row.ProgramLevel.color ? {
                backgroundColor: row.ProgramLevel.color,
                color: 'white',
              } : undefined}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">—</Typography>
          )}
          {/* Stream-colour dot — hints at the class's stream (no Stream column). */}
          {row.Stream && (
            <Box
              component="span"
              title={`Stream: ${row.Stream.name}`}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: row.Stream.color ?? 'text.disabled',
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          )}
        </Box>
      ),
    },
  ];

  // Handlers
  const handleAdd = () => {
    setEditingLesson(null);
    setFormData({
      ...DEFAULT_FORM_DATA,
      termId: '',
      termIds: [],
    });
    setModalOpen(true);
  };

  const handleView = (lesson: GroupedLesson) => {
    navigate(`${basePath}/classes/${lesson.id}`);
  };

  const handleEdit = (lesson: GroupedLesson) => {
    setEditingLesson(lesson);
    setSelectedLocationId(lesson.Room.Location.id);
    // Populate termIds with all terms this lesson spans
    const allTermIds = lesson.termLessons.map((tl) => tl.termId);
    setFormData({
      name: lesson.name,
      description: lesson.description || '',
      lessonTypeId: lesson.lessonTypeId,
      termId: lesson.termId,
      termIds: allTermIds, // Include all terms the lesson spans
      teacherId: lesson.teacherId,
      locationId: lesson.Room.Location.id,
      roomId: lesson.roomId,
      instrumentId: lesson.instrumentId || '',
      programLevelId: lesson.programLevelId || '',
      streamId: lesson.streamId || '',
      hasSchedule: lesson.hasSchedule ?? true,
      dayOfWeek: lesson.hasSchedule ? lesson.dayOfWeek : null,
      startTime: lesson.hasSchedule ? lesson.startTime : null,
      durationMins: lesson.hasSchedule ? lesson.durationMins : null,
      maxStudents: lesson.maxStudents,
      isRecurring: lesson.isRecurring,
      isActive: lesson.isActive,
      // Hybrid pattern (API returns HybridLessonPattern, fallback to HybridPattern)
      patternType: (lesson.HybridLessonPattern ?? lesson.HybridPattern)?.patternType || 'ALTERNATING',
      hybridCombination: (lesson.HybridLessonPattern ?? lesson.HybridPattern)?.hybridCombination || 'GROUP_INDIVIDUAL',
      groupWeeks: ((lesson.HybridLessonPattern ?? lesson.HybridPattern)?.groupWeeks as number[]) || [],
      individualWeeks: ((lesson.HybridLessonPattern ?? lesson.HybridPattern)?.individualWeeks as number[]) || [],
      bandLessonId: (lesson.HybridLessonPattern ?? lesson.HybridPattern)?.bandLessonId || '',
      bandWeeks: ((lesson.HybridLessonPattern ?? lesson.HybridPattern)?.bandWeeks as number[]) || [],
      individualSlotDuration: (lesson.HybridLessonPattern ?? lesson.HybridPattern)?.individualSlotDuration || 30,
      bookingDeadlineHours: (lesson.HybridLessonPattern ?? lesson.HybridPattern)?.bookingDeadlineHours || 24,
      // Band term
      classWeeksPatternType: (ownClassWeeks(lesson)?.patternType as ClassWeeksPatternType) || 'EVERY_WEEK',
      classWeeks: ownClassWeeks(lesson)?.weeks ?? [],
      // Partner classes — pre-select the current pairing (if any).
      partnerLessonId: lesson.partnerLessonId || '',
      // Mid-term window: the ANCHOR term's own row, not the arbitrary seeding row
      ...(seededWindowRef.current = storedWindowOf(lesson, resolveAnchorTermId(lesson, terms, isLocked))),
    });
    setModalOpen(true);
  };

  const handleDelete = (lesson: GroupedLesson) => {
    setLessonToDelete(lesson);
    setDeleteDialogOpen(true);
  };

  // Form data change handler
  const handleFormDataChange = (updates: Partial<LessonFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Calculate end time for submit
  const endTime = useMemo(
    () =>
      formData.hasSchedule && formData.startTime && formData.durationMins
        ? calculateEndTime(formData.startTime, formData.durationMins)
        : null,
    [formData.hasSchedule, formData.startTime, formData.durationMins]
  );

  // Cross-term edit scope: when editing a class that spans 2+ terms, the admin picks which terms
  // the edit touches (This term / This and future) via EditScopeModal. handleSubmit (the dialog's
  // onSubmit) opens the modal and defers; the modal's choice runs doSubmit(scope). The modal is
  // SKIPPED when no EXISTING unlocked row would be written (an add-only edit of an all-locked
  // class): there is nothing to scope, and asking "this term or this and future?" would mislead.
  const handleSubmit = async () => {
    const spansMultipleTerms = !!editingLesson && editingLesson.termLessons.length >= 2;
    const writesExistingRows =
      !!editingLesson &&
      filterTermsByScope(
        editingLesson.termLessons.map((tl) => tl.termId).filter((id) => formData.termIds.includes(id)),
        'all',
        anchorTermId,
        terms,
        isLocked
      ).length > 0;
    if (spansMultipleTerms && writesExistingRows) {
      setScopeModalOpen(true); // defer — doSubmit runs on the scope choice
      return;
    }
    await doSubmit('all'); // single-term edit or create: no scope needed, touch everything
  };

  // `partnerOverride` (from the offer-to-partner modal's [Make partners]) forces the partner id for
  // this submit, bypassing formData (which setState hasn't flushed yet in the same tick).
  // `awayOverride` (from the time-off warning's [Schedule anyway]) does the same for the narrow
  // approved-time-off exception — it forgives ONLY a TEACHER_UNAVAILABLE conflict server-side.
  const doSubmit = async (scope: EditScope, partnerOverride?: string, awayOverride?: boolean) => {
    setConflictError(null);
    try {
      // Build base lesson data (without termId - we'll handle that separately)
      const baseLessonData = {
        name: formData.name,
        description: formData.description || undefined,
        lessonTypeId: formData.lessonTypeId,
        teacherId: formData.teacherId,
        roomId: formData.roomId,
        instrumentId: formData.instrumentId || undefined,
        programLevelId: formData.programLevelId || undefined,
        streamId: formData.streamId || undefined,
        hasSchedule: formData.hasSchedule,
        dayOfWeek: formData.hasSchedule ? (formData.dayOfWeek ?? undefined) : undefined,
        startTime: formData.hasSchedule ? (formData.startTime ?? undefined) : undefined,
        endTime: formData.hasSchedule ? (endTime ?? undefined) : undefined,
        durationMins: formData.hasSchedule ? (formData.durationMins ?? undefined) : undefined,
        maxStudents: formData.maxStudents,
        isRecurring: formData.isRecurring,
        hybridPattern: undefined as CreateLessonInput['hybridPattern'],
        classWeeks: undefined as CreateLessonInput['classWeeks'],
        overrideConflicts: overrideConflicts || undefined,
        // Narrow approved-time-off exception, set only by [Schedule anyway] on the amber warning.
        // Rides baseLessonData so it reaches BOTH the update loop and the create payload.
        allowAwayOverride: awayOverride || undefined,
      };

      // Add hybrid pattern for HYBRID type
      if (selectedLessonType?.type === 'HYBRID') {
        const combo = formData.hybridCombination;
        baseLessonData.hybridPattern = {
          patternType: formData.patternType,
          hybridCombination: combo,
          groupWeeks: combo === 'INDIVIDUAL_BAND' ? [] : formData.groupWeeks,
          individualWeeks: combo === 'GROUP_BAND' ? [] : formData.individualWeeks,
          bandLessonId: combo !== 'GROUP_INDIVIDUAL' ? formData.bandLessonId || null : null,
          individualSlotDuration: formData.individualSlotDuration,
          bookingDeadlineHours: formData.bookingDeadlineHours,
        };
      }

      // Class weeks for every plain type (2026-10-16): INDIVIDUAL / GROUP choose their weeks
      // (fortnightly, say) the way a BAND chooses its rehearsals.
      // ⚠️ Gated on the lesson TYPE, not on the weeks being non-empty (#177). The old
      // `rehearsalWeeks.length > 0` guard made "switch this band to Every Week" a silent
      // no-op: the switch clears the array, so no payload was built, so the backend's
      // `data.bandTerm !== undefined` gate was never entered and the row stayed CUSTOM —
      // with a success toast. EVERY_WEEK legitimately carries an empty array (a band's
      // weeks are derived per term via calculateBandWeeks; an INDIVIDUAL / GROUP class
      // writes no row at all, D2).
      // An empty CUSTOM list builds NO payload. Since class weeks Phase 7 the dialog BLOCKS
      // Save on it with an inline error (#179 option 1), so that half is a backstop now;
      // the server would 400 the list.
      if (
        ['INDIVIDUAL', 'GROUP', 'BAND'].includes(selectedLessonType?.type ?? '') &&
        (formData.classWeeksPatternType === 'EVERY_WEEK' || formData.classWeeks.length > 0)
      ) {
        baseLessonData.classWeeks = {
          patternType: formData.classWeeksPatternType,
          weeks: formData.classWeeksPatternType === 'EVERY_WEEK' ? [] : formData.classWeeks,
        };
      }

      if (editingLesson) {
        // When editing a grouped lesson: update all existing term lessons + handle term changes
        const existingTermIds = editingLesson.termLessons.map((tl) => tl.termId);
        const newTermIds = formData.termIds;

        // Find terms to add (in new but not in existing)
        const termsToAdd = newTermIds.filter((id) => !existingTermIds.includes(id));
        // Terms to remove (deselected). LOCKED terms are excluded — deselecting a locked term must NOT
        // hard-delete its read-only history (a whole-class delete is the separate explicit action).
        const termsToRemove = termsToRemoveForEdit(existingTermIds, newTermIds, terms, isLocked);
        // Find terms to update (in both)
        const termsToUpdateAll = existingTermIds.filter((id) => newTermIds.includes(id));

        // Apply the chosen edit scope to which EXISTING terms get updated. Adds are explicit
        // membership changes (the dialog blocks selecting a LOCKED term; the server 409s it); removes of
        // locked terms are excluded above; scope filters the update loop; locked terms are never
        // updated regardless. An all-locked class therefore updates nothing and removes nothing: the
        // only write is the one bulkCreate below, from the (disabled, so unchanged) form.
        const termsToUpdate = filterTermsByScope(termsToUpdateAll, scope, anchorTermId, terms, isLocked);

        // Get all lesson IDs in this grouped lesson (to exclude from conflict checks)
        const allLessonIds = editingLesson.termLessons.map((tl) => tl.id);

        // Resend the hybrid pattern to the sibling-term rows ONLY if the admin changed it
        // (#174). The dialog is seeded from ONE row; pushing that row's weeks to every
        // sibling on a room/time/name edit is a ratchet across terms of different lengths
        // — the server clamps per term, so a 9-week sibling's pattern would silently shrink
        // an 11-week sibling to 9 sessions. Omitted = each row keeps its own weeks.
        const storedPattern = editingLesson.HybridLessonPattern ?? editingLesson.HybridPattern ?? null;
        const resendHybridPattern =
          baseLessonData.hybridPattern !== undefined && !hybridPatternUnchanged(formData, storedPattern);
        // Same rule for a band's rehearsal weeks (#173): resend `bandTerm` to the sibling
        // terms only when the admin changed it — the server clamps per term, so pushing
        // one term's CUSTOM list everywhere on a room edit would ratchet the siblings.
        const resendClassWeeks =
          baseLessonData.classWeeks !== undefined &&
          !classWeeksUnchanged(formData, ownClassWeeks(editingLesson), selectedLessonType?.type);

        // The class window (start / last week) lives on the ANCHOR term's row only, and is sent
        // only when the admin changed it — same rule as the pattern above (#174), or a room edit
        // would copy the anchor's window onto every sibling term.
        const windowIsUnchanged =
          windowUnchanged(formData, editingLesson, anchorTermId) || sameWindow(formData, seededWindowRef.current);

        // Partner classes: pairing is 1:1 and per-term. The picker's candidates are scoped to the
        // ANCHOR term, so only apply partnerLessonId to THAT term's row — sibling-term rows leave it
        // untouched (undefined). '' from the picker means "unpair" → send null; a chosen id → that
        // id. The anchor is always a live term that survives the scope filter below, so the partner
        // write can't silently target a row the update loop skips.
        const partnerForPrimaryTerm =
          selectedLessonType?.type === 'INDIVIDUAL' && selectedLessonType?.allowPartnerOverlap
            ? (partnerOverride ?? (formData.partnerLessonId ? formData.partnerLessonId : null))
            : undefined;

        // Sent invoices that may need amending, collected from every update in the loop.
        // Opened once, after the loop — and also if a later step fails, because the window
        // change has already been saved by then.
        const invoicesToAmend = new Map<string, InvoiceToAmend>();
        // The terms whose update returned an invoice to check. The prompt describes the ANCHOR
        // term's change; when another term contributed, it names the terms instead (/qa B-M2).
        const invoiceTermIds = new Set<string>();
        // A caller without invoice read gets no list, only the flag (roles R17).
        let invoicesMayNeedAttention = false;
        const openAmendPrompt = () => {
          billingNotice('window', invoicesMayNeedAttention);
          if (invoicesToAmend.size === 0) return;
          const anchorTerm = terms.find((t) => t.id === anchorTermId);
          const anchorLesson = editingLesson.termLessons.find((tl) => tl.termId === anchorTermId);
          if (!anchorTerm || !anchorLesson) return;
          const weeks = termWeekCount(anchorTerm.startDate, anchorTerm.endDate);
          const otherTermContributed = [...invoiceTermIds].some((id) => id !== anchorTermId);
          const termNames = otherTermContributed
            ? terms
                .filter((t) => invoiceTermIds.has(t.id))
                .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
                .map((t) => t.name)
            : undefined;
          setAmendPrompt({
            invoices: [...invoicesToAmend.values()],
            context: {
              kind: 'class',
              lessonId: anchorLesson.id,
              lessonName: formData.name,
              termName: anchorTerm.name,
              newWindow: lessonWeekWindow(
                { startWeek: formData.startWeek || null, endWeek: formData.endWeek || null },
                weeks
              ),
              previousWindow: lessonWeekWindow(seededWindowRef.current, weeks),
              // A weeks-only change keeps the window: name the chosen weeks (fortnightly etc.).
              newWeeks: classWeeksForWording(selectedLessonType?.type, formData.classWeeksPatternType, formData.classWeeks),
              previousWeeks: classWeeksForWording(
                editingLesson.LessonType?.type,
                ownClassWeeks(editingLesson)?.patternType,
                ownClassWeeks(editingLesson)?.weeks
              ),
              ...(termNames && { termNames }),
            },
          });
        };

        // Update all existing lessons that are being kept
        try {
        for (const termId of termsToUpdate) {
          const lessonRecord = editingLesson.termLessons.find((tl) => tl.termId === termId);
          if (lessonRecord) {
            const updated = await updateMutation.mutateAsync({
              id: lessonRecord.id,
              data: {
                ...baseLessonData,
                hybridPattern: resendHybridPattern ? baseLessonData.hybridPattern : undefined,
                classWeeks: resendClassWeeks ? baseLessonData.classWeeks : undefined,
                termId,
                isActive: formData.isActive,
                excludeLessonIds: allLessonIds,
                // Only the viewed term carries the partner change; others stay untouched.
                partnerLessonId: termId === anchorTermId ? partnerForPrimaryTerm : undefined,
                ...windowForUpdate(termId, anchorTermId, formData, windowIsUnchanged),
              },
            });
            for (const invoice of updated?.invoicesToAmend ?? []) invoicesToAmend.set(invoice.id, invoice);
            if ((updated?.invoicesToAmend?.length ?? 0) > 0) invoiceTermIds.add(termId);
            if (updated?.invoicesMayNeedAttention) invoicesMayNeedAttention = true;
          }
        }

        // Hard delete lessons for removed terms (they shouldn't exist anymore)
        for (const termId of termsToRemove) {
          const lessonRecord = editingLesson.termLessons.find((tl) => tl.termId === termId);
          if (lessonRecord) {
            await deleteMutation.mutateAsync({ id: lessonRecord.id, hardDelete: true });
          }
        }

        // Create new lessons for added terms
        if (termsToAdd.length > 0) {
          await bulkCreateMutation.mutateAsync({
            termIds: termsToAdd,
            lessonData: baseLessonData,
            excludeLessonIds: allLessonIds, // Exclude all grouped lessons from conflict checks
          });
        }
        } finally {
          openAmendPrompt();
        }
      } else {
        // When creating: use bulk create if multiple terms, single create otherwise
        if (formData.termIds.length > 1) {
          // The window applies to the FIRST selected term only (the dialog's anchor); the server
          // saves every other term as a full-term class. No window → no `windowTermId`.
          const createWindow = windowForCreate(formData);
          await bulkCreateMutation.mutateAsync({
            termIds: formData.termIds,
            lessonData: { ...baseLessonData, ...createWindow },
            ...(Object.keys(createWindow).length > 0 ? { windowTermId: formData.termIds[0] } : {}),
          });
        } else {
          const createPayload = {
            ...baseLessonData,
            ...windowForCreate(formData),
            termId: formData.termIds[0],
            // Partner classes: single-term INDIVIDUAL create can pair with an existing eligible
            // lesson (e.g. via the offer-to-partner modal). Only for a partner-enabled type.
            partnerLessonId:
              selectedLessonType?.type === 'INDIVIDUAL' && selectedLessonType?.allowPartnerOverlap
                ? (partnerOverride ?? formData.partnerLessonId ?? undefined)
                : undefined,
          };
          await createMutation.mutateAsync(createPayload);
        }
      }
      setConflictError(null);
      setOverrideConflicts(false);
      setModalOpen(false);
    } catch (err: unknown) {
      const response = (err as { response?: { status?: number; data?: {
        message?: string;
        details?: {
          conflictLessonId?: string;
          conflictLessonName?: string;
          conflictSource?: string;
          overridable?: boolean;
          teacherName?: string;
          awayLabel?: string;
          awayFrom?: string;
          awayTo?: string;
        };
      } } })?.response;
      const status = response?.status;
      const message = response?.data?.message || (err as { message?: string })?.message;
      // A refused window change is not a scheduling conflict: no override, no partner offer.
      const refused = parseWindowHasData(err);
      if (refused) {
        setWindowRefusal(refused);
        return;
      }
      // A locked term is not a scheduling conflict (no override, no partner offer): the mutation's
      // snackbar already shows the server's sentence, which names the reopen path.
      if (isTermLockedError(err)) return;
      if (status === 409) {
        // Teacher time off: an APPROVED away period is a soft conflict — the admin may legitimately
        // want to schedule into it (the teacher agreed to cover, the dates are about to change).
        // Show an AMBER warning offering [Schedule anyway] instead of the red blocking toast.
        // Checked BEFORE the partner offer because an away conflict is never partner-eligible.
        // `awayOverride` guards the retry: if we already sent the flag and STILL got a 409, the
        // conflict is something else (a real double-booking) — fall through to the hard block
        // rather than re-offering an override that just failed.
        const awayDetails = response?.data?.details;
        if (!awayOverride && awayDetails?.conflictSource === 'TEACHER_UNAVAILABLE' && awayDetails?.overridable) {
          setAwayOffer({
            teacherName: awayDetails.teacherName,
            awayLabel: awayDetails.awayLabel,
            awayFrom: awayDetails.awayFrom,
            awayTo: awayDetails.awayTo,
            scope,
          });
          return; // defer — the warning dialog drives the next step
        }

        // Offer-to-partner: when the 409 is against a partner-ELIGIBLE lesson (same teacher/term/day,
        // both partner-enabled INDIVIDUAL, candidate free), offer to pair instead of a bare toast.
        // Requires the INCOMING side to also be partner-enabled INDIVIDUAL. Only when we're NOT
        // already re-submitting with this exact partner (that path lands here only if a THIRD,
        // different lesson still conflicts — see below → falls through to the normal conflict).
        const details = response?.data?.details;
        const conflictLessonId = details?.conflictLessonId;
        const incomingEligible =
          selectedLessonType?.type === 'INDIVIDUAL' && !!selectedLessonType?.allowPartnerOverlap;
        const alreadyTryingThisPartner = formData.partnerLessonId === conflictLessonId && !!conflictLessonId;

        if (conflictLessonId && incomingEligible && !alreadyTryingThisPartner) {
          const conflictLesson = (lessonsData ?? []).find((l) => l.id === conflictLessonId);
          const self = {
            id: editingLesson?.id,
            teacherId: formData.teacherId,
            termId: editingLesson ? anchorTermId : formData.termIds[0],
            dayOfWeek: formData.dayOfWeek,
          };
          // Create-offer is single-term only (bulk rejects partner).
          const createIsSingleTerm = editingLesson ? true : formData.termIds.length === 1;
          if (conflictLesson && createIsSingleTerm && isPartnerEligible(conflictLesson, self)) {
            setPartnerOffer({
              conflictLessonId,
              conflictLessonName: details?.conflictLessonName || conflictLesson.name,
              scope,
            });
            return; // defer — the offer modal drives the next step
          }
        }
        setConflictError(message || 'A scheduling conflict was detected.');
      }
      // Other errors are handled by the mutation hook (snackbar etc.)
    }
  };

  // Hard delete every term-lesson behind this grouped row. `force` additionally leaves any
  // hybrid classes that reference a band lesson in a "band TBD" state (see backend deleteLesson).
  //
  // The first (non-force) pass can partially succeed: if the band is referenced in only some
  // terms, the unreferenced terms get deleted before a referenced term throws 409. On the force
  // retry we therefore tolerate a 404 per term — those already-deleted terms are simply skipped,
  // so the retry reliably reaches and force-deletes the still-present referenced term(s).
  const deleteAllTermLessons = async (lesson: GroupedLesson, force: boolean) => {
    for (const termLesson of lesson.termLessons) {
      try {
        await deleteMutation.mutateAsync({ id: termLesson.id, hardDelete: true, force });
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        // On the force retry, a term deleted in the first pass is now gone → 404. Skip it.
        if (force && status === 404) continue;
        throw err;
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!lessonToDelete) return;
    try {
      // Hard delete all lessons across all terms (permanently removes them).
      // User must first set lesson to inactive before deleting, so this is a permanent action.
      await deleteAllTermLessons(lessonToDelete, false);
      setDeleteDialogOpen(false);
      setLessonToDelete(null);
    } catch (err: unknown) {
      // A band lesson still referenced by hybrid classes → 409. Offer a second-stage
      // confirm that detaches those classes and force-deletes. Other errors surface via
      // the mutation's snackbar.
      const status = (err as { response?: { status?: number } })?.response?.status;
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (status === 409 && message) {
        setDeleteDialogOpen(false);
        setBandDetachMessage(message);
      }
    }
  };

  const handleConfirmBandDetach = async () => {
    if (!lessonToDelete) return;
    try {
      await deleteAllTermLessons(lessonToDelete, true);
      setBandDetachMessage(null);
      setLessonToDelete(null);
    } catch {
      // Error is handled by the mutation's snackbar.
    }
  };

  const handleCancelBandDetach = () => {
    setBandDetachMessage(null);
    setLessonToDelete(null);
  };

  // Check if lesson can be deleted (must be inactive)
  const canDeleteLesson = (lesson: GroupedLesson) => !lesson.isActive;

  const isFormLoading = createMutation.isPending || bulkCreateMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Classes"
        subtitle={isTeacherView ? 'View class schedules' : 'Manage classes and student enrollments'}
        actionLabel={isTeacherView ? undefined : 'Add Class'}
        onAction={isTeacherView ? undefined : handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load classes. Please try again.
        </Alert>
      )}

      {/* View Mode Toggle (Teacher view only) */}
      {isTeacherView && (
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="mine">
              <PersonIcon sx={{ mr: 0.5 }} fontSize="small" />
              My Classes
            </ToggleButton>
            <ToggleButton value="all">
              <SchoolIcon sx={{ mr: 0.5 }} fontSize="small" />
              All School
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Filters */}
      <LessonFiltersPanel
        filters={filters}
        onFiltersChange={setFilters}
        terms={terms}
        lessonTypes={lessonTypes}
        teachers={teachers}
        instruments={instruments}
        showTeacherFilter={!isTeacherView || viewMode === 'all'}
      />

      <DataTable
        columns={columns}
        data={groupedLessons}
        loading={isLoading}
        onView={handleView}
        onEdit={isTeacherView ? undefined : handleEdit}
        onDelete={isTeacherView ? undefined : handleDelete}
        canDelete={canDeleteLesson}
        deleteDisabledTooltip="Set status to Inactive to delete this class"
        emptyMessage={isTeacherView ? 'No classes found.' : "No classes found. Click 'Add Class' to create one."}
        searchPlaceholder="Search classes..."
      />

      {/* Add/Edit Modal */}
      <LessonDialog
        open={modalOpen}
        editingLesson={editingLesson}
        formData={formData}
        onFormDataChange={handleFormDataChange}
        onSubmit={handleSubmit}
        onClose={() => { setModalOpen(false); setConflictError(null); setOverrideConflicts(false); setPartnerOffer(null); }}
        isLoading={isFormLoading}
        terms={terms}
        isLocked={isLocked}
        lockReasonOf={lockReasonOf}
        isEnded={isEnded}
        locations={locations}
        rooms={rooms}
        instruments={instruments}
        programLevels={programLevels}
        streams={streams}
        lessonTypes={lessonTypes}
        teachers={teachers}
        durations={durations}
        selectedLessonType={selectedLessonType}
        partnerCandidates={partnerCandidates}
        conflictError={conflictError}
        overrideConflicts={overrideConflicts}
        onOverrideConflictsChange={setOverrideConflicts}
      />

      {/* Refused window change: the weeks already hold attendance / bookings / make-ups */}
      <WindowHasDataDialog info={windowRefusal} onClose={() => setWindowRefusal(null)} />

      {/* Sent invoices that MAY need amending after a class window change */}
      {amendPrompt && (
        <InvoicesToAmendDialog
          open
          onClose={() => setAmendPrompt(null)}
          invoices={amendPrompt.invoices}
          context={amendPrompt.context}
        />
      )}

      {/* Cross-term edit scope (only shown when editing a class spanning 2+ terms) */}
      <EditScopeModal
        open={scopeModalOpen}
        currentTermName={terms.find((t) => t.id === anchorTermId)?.name ?? 'this term'}
        loading={isFormLoading}
        onCancel={() => setScopeModalOpen(false)}
        onConfirm={async (scope) => {
          setScopeModalOpen(false);
          await doSubmit(scope);
        }}
      />

      {/* Offer to make a conflicting eligible lesson a partner class instead of a hard conflict */}
      <PartnerOfferDialog
        open={!!partnerOffer}
        conflictLessonName={partnerOffer?.conflictLessonName ?? ''}
        // Warn if pairing would replace an existing DIFFERENT partner on the edited lesson.
        currentPartnerName={
          editingLesson?.Partner && editingLesson.Partner.id !== partnerOffer?.conflictLessonId
            ? editingLesson.Partner.name
            : null
        }
        loading={isFormLoading}
        onMakePartners={async () => {
          const offer = partnerOffer;
          if (!offer) return;
          setPartnerOffer(null);
          // Persist the pairing in the form (so a subsequent 409 against a THIRD lesson knows we
          // already tried this partner) AND pass it as an override for this immediate re-submit.
          handleFormDataChange({ partnerLessonId: offer.conflictLessonId });
          await doSubmit(offer.scope, offer.conflictLessonId);
        }}
        onItsAConflict={() => {
          // Treat as a real conflict → fall to the normal override path (the conflict Alert + switch).
          setPartnerOffer(null);
          setConflictError('A scheduling conflict was detected.');
        }}
      />

      {/* Teacher time off — amber soft warning, not a hard block */}
      <TimeOffWarningDialog
        open={!!awayOffer}
        teacherName={awayOffer?.teacherName}
        awayLabel={awayOffer?.awayLabel}
        awayFrom={awayOffer?.awayFrom}
        awayTo={awayOffer?.awayTo}
        loading={isFormLoading}
        onScheduleAnyway={async () => {
          const offer = awayOffer;
          if (!offer) return;
          setAwayOffer(null);
          // Re-submit with the NARROW away override only — deliberately not overrideConflicts,
          // so a room/lesson clash in the same save still blocks.
          await doSubmit(offer.scope, undefined, true);
        }}
        onCancel={() => {
          // Back out — the edit dialog stays open so the admin can pick another day/time.
          setAwayOffer(null);
        }}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Deactivate Class"
        message={`Are you sure you want to deactivate "${lessonToDelete?.name}"? Students will be unenrolled and this class will no longer appear in schedules.`}
        confirmLabel="Deactivate"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      {/* Band detach confirmation — shown when the band is referenced by hybrid classes */}
      <ConfirmDialog
        open={bandDetachMessage !== null}
        title="Remove band from these classes?"
        message={
          `${bandDetachMessage ?? ''}\n\n` +
          'These classes keep their structure but their band becomes unassigned (shown as ' +
          '"Band: TBD"). While unassigned, band sessions are paused — not scheduled or billed ' +
          '— until you assign a new band to each class. This cannot be undone.'
        }
        confirmLabel="Delete band"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmBandDetach}
        onCancel={handleCancelBandDetach}
      />
    </Box>
  );
}
